import {
  guessOption,
  optionLabel,
  searchCountries,
  type CountryOption,
} from '../../../shared/feed/countries';
import { $, attr } from './dom';

/**
 * The dashboard's country picker (web/src/shell/CountryCombobox.tsx) for a
 * page without React, behaving as it does with `openOnFocus`: the WAI-ARIA
 * combobox pattern, type to filter, arrows to move, Enter to choose, Escape
 * to back out. The names, the order, the search and the guess are the same
 * functions (src/shared/feed/countries.ts); the markup is the server's
 * (views/get-started.hbs) and the classes the dashboard's.
 *
 * `value` '' means "nothing picked"; the first option then stands for the
 * guess, labelled with the guessed country.
 */
export interface CountryPicker {
  /** Draws the choice and the guess as they now stand. */
  set(value: string, guess: string | null): void;
}

export function countryCombobox(
  root: HTMLElement,
  all: readonly CountryOption[],
  initial: { value: string; guess: string | null },
  onChange: (value: string) => void,
): CountryPicker | null {
  const input = $<HTMLInputElement>(root, 'input[role="combobox"]');
  const list = $<HTMLUListElement>(root, '[role="listbox"]');
  if (!input || !list) return null;
  const listId = list.id;

  let { value, guess } = initial;
  let open = false;
  let query = '';
  let active = 0;
  let shown: CountryOption[] = [];

  const selected = () =>
    all.find((c) => c.value === value) ?? (value ? null : guessOption(guess));

  // The guess option only makes sense unfiltered: typing means looking for a
  // specific country.
  const options = () =>
    query.trim() ? searchCountries(all, query) : [guessOption(guess), ...all];

  const paintActive = () => {
    Array.from(list.children).forEach((li, i) =>
      li.classList.toggle('is-active', i === active),
    );
    if (open && shown[active]) {
      attr(input, 'aria-activedescendant', `${listId}-${active}`);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const paint = () => {
    const choice = selected();
    input.placeholder = choice ? optionLabel(choice) : 'Search';
    const text = open ? query : choice ? optionLabel(choice) : '';
    if (input.value !== text) input.value = text;
    attr(input, 'aria-expanded', String(open));
    list.hidden = !open;
    if (!open) {
      shown = [];
      list.replaceChildren();
      paintActive();
      return;
    }
    shown = options();
    if (shown.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'combobox-empty';
      empty.textContent = `No country matches “${query}”`;
      list.replaceChildren(empty);
    } else {
      list.replaceChildren(
        ...shown.map((option, index) => {
          const li = document.createElement('li');
          li.id = `${listId}-${index}`;
          attr(li, 'role', 'option');
          attr(li, 'aria-selected', String(option.value === value));
          const flag = document.createElement('span');
          attr(flag, 'aria-hidden', 'true');
          flag.textContent = option.flag;
          li.append(flag, option.name);
          return li;
        }),
      );
    }
    paintActive();
  };

  const show = () => {
    open = true;
    query = '';
    const index = options().findIndex((o) => o.value === value);
    active = Math.max(0, index);
    paint();
  };

  const close = () => {
    open = false;
    query = '';
    paint();
  };

  const choose = (option: CountryOption) => {
    close();
    input.blur();
    if (option.value !== value) onChange(option.value);
  };

  const scrollTo = (index: number) => {
    active = index;
    paintActive();
    document
      .getElementById(`${listId}-${index}`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  input.addEventListener('focus', show);
  input.addEventListener('click', () => {
    if (!open) show();
  });
  input.addEventListener('blur', close);
  input.addEventListener('input', () => {
    query = input.value;
    active = 0;
    open = true;
    paint();
  });
  input.addEventListener('keydown', (event) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        show();
      }
      return;
    }
    const last = shown.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        scrollTo(active >= last ? 0 : active + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        scrollTo(active <= 0 ? last : active - 1);
        break;
      case 'Home':
      case 'End':
        if (!query) {
          event.preventDefault();
          scrollTo(event.key === 'Home' ? 0 : last);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (shown[active]) choose(shown[active]);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
    }
  });

  const indexOf = (target: EventTarget | null) => {
    const li = (target as Element | null)?.closest('[role="option"]');
    return li ? Array.from(list.children).indexOf(li) : -1;
  };
  // mousedown, not click: the input's blur would close the list before a
  // click could land.
  list.addEventListener('mousedown', (event) => {
    const index = indexOf(event.target);
    if (!shown[index]) return;
    event.preventDefault();
    choose(shown[index]);
  });
  list.addEventListener('mousemove', (event) => {
    const index = indexOf(event.target);
    if (index >= 0 && index !== active) {
      active = index;
      paintActive();
    }
  });

  paint();
  return {
    set(nextValue, nextGuess) {
      value = nextValue;
      guess = nextGuess;
      paint();
    },
  };
}
